import { useId, useState, type FormEvent, type KeyboardEvent } from 'react';

import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';

import {
  WARNING_LABELS,
  WARNING_TYPES,
  warningLabel,
  warningsOf,
  type Quote,
  type QuoteWarning,
  type QuoteWarningPatch,
  type WarningType,
} from './types';
import { useEditWarning, useSetWarnings } from './useQuotes';

/**
 * What the supplier wrote, and what this system could not read.
 *
 * **Every one is shown and none is ever hidden.** Contexto §4.4: if the supplier wrote it,
 * it is because it matters, and the ones this system raises ("no he podido leer el precio
 * de la línea 3", "mi cuenta no cuadra con el total que imprimen") are the ones that stop a
 * wrong number leaving the building. Collapsing them behind a "ver más" would be the same
 * as dropping them, because nobody opens it.
 *
 * The checkbox is Víctor's answer to "¿esto va en el documento?", which is a different
 * question from "¿esto importa?". A condition of sale can matter enormously and belong in a
 * phone call rather than on the PDF.
 *
 * The checkbox writes on the click (`PUT /api/quotes/{id}/warnings`, contract v7.2), like
 * the customer picker: there is nothing to batch, and a tick that waited for "Guardar
 * líneas" would be a tick somebody forgot. Only on a draft; after the approval the
 * document is what it is.
 *
 * «Editar» (contract v10.6) opens the note in place: its type and its text, the text being
 * what prints. The extraction drops an accent, the supplier writes in his own shorthand,
 * and the customer should read a sentence. The supplier's words are not lost by it: the
 * back keeps them in `original_text` from the first rewrite, this card shows them under
 * the note, and «Recuperar» puts them back. One note at a time, saved on «Guardar» and
 * not on blur: a text that goes on a customer's document is not something to write by
 * accident while reaching for the checkbox.
 */
export function Warnings({
  quote,
  showPurchase = true,
  className,
}: {
  quote: Quote;
  /** Off in the counter mode (contract v10.47): the system's own notes compare what it
   * computed from the cost with what the supplier printed, and the references and the
   * supplier's own words are the supplier's. What goes on the customer's document stays. */
  showPurchase?: boolean;
  className?: string;
}) {
  const setWarnings = useSetWarnings(quote.id);
  const editWarning = useEditWarning(quote.id);
  const [editing, setEditing] = useState<number | null>(null);
  const warnings = warningsOf(quote);
  if (warnings.length === 0) return null;

  const editable = quote.status === 'draft';
  // Positions are into the API's list, not into `fromSupplier`: the ones this system
  // raised sit in the same list and the API counts them.
  const includedPositions = warnings.flatMap((each, index) => (each.include ? [index] : []));
  const fromSupplier = warnings.flatMap((each, index) =>
    each.source !== 'extraccion' ? [{ warning: each, index }] : [],
  );
  const fromUs = showPurchase ? warnings.filter((each) => each.source === 'extraccion') : [];

  function toggle(index: number, include: boolean): void {
    const next = include
      ? [...includedPositions, index]
      : includedPositions.filter((each) => each !== index);
    setWarnings.mutate(next);
  }

  function open(index: number): void {
    editWarning.reset();
    setEditing(index);
  }

  function close(): void {
    editWarning.reset();
    setEditing(null);
  }

  function save(index: number, payload: QuoteWarningPatch): void {
    editWarning.mutate({ position: index, payload }, { onSuccess: () => setEditing(null) });
  }

  return (
    <Card
      title={`Avisos (${warnings.length})`}
      {...(editable && fromSupplier.length > 0
        ? { description: 'Marca los que deban salir en el documento.' }
        : {})}
      {...(fromUs.length > 0 ? { aside: <Badge tone="warn">revisar</Badge> } : {})}
      {...(className ? { className } : {})}
      bodyClassName="gap-2"
    >
      {fromUs.map((warning, index) => (
        <Callout key={`propio-${index}`} compact tone="warn">
          {warning.text}
        </Callout>
      ))}

      {fromSupplier.length > 0 ? (
        <ul className="divide-line-2 flex flex-col divide-y">
          {fromSupplier.map(({ warning, index }) =>
            editing === index ? (
              <li key={`proveedor-${index}`} className="py-2 first:pt-0 last:pb-0">
                <WarningEditor
                  warning={warning}
                  pending={editWarning.isPending}
                  error={editWarning.isError ? editWarning.error : null}
                  onSave={(payload) => save(index, payload)}
                  onCancel={close}
                />
              </li>
            ) : (
              <li
                key={`proveedor-${index}`}
                className="flex items-start gap-2.5 py-1.5 text-[13.5px] first:pt-0 last:pb-0"
              >
                {editable ? (
                  <input
                    type="checkbox"
                    className="accent-accent mt-0.5 size-4 shrink-0"
                    aria-label="va en el presupuesto"
                    title="va en el presupuesto"
                    checked={Boolean(warning.include)}
                    disabled={setWarnings.isPending}
                    onChange={(event) => toggle(index, event.target.checked)}
                  />
                ) : null}
                <span className="text-ink min-w-0 flex-1">
                  <Badge tone="neutral" className="mr-1.5 align-[1px]">
                    {warningLabel(warning.type)}
                  </Badge>
                  {warning.text}
                  {warning.line && showPurchase ? (
                    // The back documents this field as "the supplier reference the note is
                    // about", not a row number: with real data it reads `HE-93380`.
                    <span className="text-ink-3"> · ref. {warning.line}</span>
                  ) : null}
                  {warning.original_text && showPurchase ? (
                    // Never hidden: what the supplier actually wrote stays in view under
                    // the sentence Víctor made of it, on the screen and not behind a hover.
                    <span className="text-ink-3 mt-0.5 block text-[12.5px] leading-snug">
                      Editado · el proveedor escribió: «{warning.original_text}»
                    </span>
                  ) : null}
                </span>
                {editable ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-my-1.5"
                    aria-label={`Editar el aviso «${warning.text ?? ''}»`}
                    disabled={setWarnings.isPending || editing !== null}
                    onClick={() => open(index)}
                  >
                    Editar
                  </Button>
                ) : (
                  <span
                    className={`shrink-0 text-[13px] ${warning.include ? 'text-ok' : 'text-ink-4'}`}
                  >
                    {warning.include ? 'va en el presupuesto' : 'no va en el presupuesto'}
                  </span>
                )}
              </li>
            ),
          )}
        </ul>
      ) : null}
      {setWarnings.isError ? (
        <ErrorMessage error={setWarnings.error}>No se pudo guardar el aviso</ErrorMessage>
      ) : null}
    </Card>
  );
}

/**
 * One note, open for rewriting. Sends only what changed, and nothing while nothing has.
 *
 * `Escape` cancels and `Ctrl+Enter` saves, because the text is a textarea and a plain
 * `Enter` there is a line break. «Recuperar» puts the supplier's words back in the box
 * and not straight into the API: a text that goes on a customer's document gets looked
 * at before it is saved, even the original.
 */
function WarningEditor({
  warning,
  pending,
  error,
  onSave,
  onCancel,
}: {
  warning: QuoteWarning;
  pending: boolean;
  error: unknown;
  onSave: (payload: QuoteWarningPatch) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const currentType = isWarningType(warning.type) ? warning.type : 'otro';
  const currentText = warning.text ?? '';
  const [type, setType] = useState<WarningType>(currentType);
  const [text, setText] = useState(currentText);

  const trimmed = text.trim();
  const payload: QuoteWarningPatch = {
    ...(type !== currentType ? { type } : {}),
    ...(trimmed !== currentText ? { text: trimmed } : {}),
  };
  const changed = Object.keys(payload).length > 0;
  const canSave = changed && trimmed.length > 0 && !pending;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (canSave) onSave(payload);
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      if (canSave) onSave(payload);
    }
  }

  return (
    <form
      onSubmit={submit}
      onKeyDown={onKeyDown}
      aria-label={`Editar el aviso «${currentText}»`}
      className="bg-paper-2 flex flex-col gap-3 rounded-md p-3"
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-type`} className="text-ink-2 text-[13.5px] font-medium">
            Tipo
          </label>
          <select
            id={`${id}-type`}
            className="field"
            value={type}
            disabled={pending}
            onChange={(event) => setType(event.target.value as WarningType)}
          >
            {WARNING_TYPES.map((each) => (
              <option key={each} value={each}>
                {WARNING_LABELS[each]}
              </option>
            ))}
          </select>
        </div>
        {warning.line ? <p className="text-ink-3 pb-2 text-[13px]">ref. {warning.line}</p> : null}
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={`${id}-text`} className="text-ink-2 text-[13.5px] font-medium">
          Texto
          <span className="text-ink-4 ml-1.5 font-normal">lo que leerá el cliente</span>
        </label>
        <textarea
          id={`${id}-text`}
          className="field h-auto min-h-20 py-2"
          rows={2}
          maxLength={500}
          autoFocus
          value={text}
          disabled={pending}
          onChange={(event) => setText(event.target.value)}
        />
        {trimmed.length === 0 ? (
          <p role="alert" className="text-danger text-[13px]">
            Un aviso sin texto no es un aviso. Si no debe salir, desmárcalo.
          </p>
        ) : null}
        {warning.original_text ? (
          <p className="text-ink-3 text-[12.5px] leading-snug">
            El proveedor escribió: «{warning.original_text}»
            {text !== warning.original_text ? (
              <>
                {' '}
                <button
                  type="button"
                  className="text-accent-2 font-medium underline-offset-2 hover:underline"
                  disabled={pending}
                  onClick={() => setText(warning.original_text ?? '')}
                >
                  Recuperar
                </button>
              </>
            ) : null}
          </p>
        ) : null}
      </div>
      {error ? <ErrorMessage error={error}>No se pudo guardar el aviso</ErrorMessage> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={!canSave}>
          {pending ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
          Cancelar
        </Button>
        <span className="text-ink-4 ml-auto text-[12px]">Ctrl+Intro guarda · Esc cancela</span>
      </div>
    </form>
  );
}

function isWarningType(value: string | null | undefined): value is WarningType {
  return typeof value === 'string' && (WARNING_TYPES as string[]).includes(value);
}
